/**
 * AuthShell — shared Códice Botánico layout + form primitives for the
 * login / register screens.
 *
 * Left: a jade-night brand aside with the colibrí, fret crest and a line
 * of philosophy. Right: a paper card holding the form. Collapses to a
 * single column on mobile. Pure CSS/native — no MUI.
 */
import React, { useState } from 'react';

export const AuthColibri = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden
    style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.35))' }}>
    <circle cx="24" cy="24" r="22" fill="#1E2C24" stroke="#C9A227" strokeWidth="1.4" />
    <path d="M19 30c-1-5 1-9 5-11 3-1.5 7-1 9 1l-3 2 4 0-2 3 3 1c-2 2-6 3-9 2" fill="#2E8B57" />
    <circle cx="18.5" cy="20" r="3.2" fill="#1F5C3D" />
    <path d="M15.5 19l-7-3 7 1.6z" fill="#C9A227" />
    <circle cx="17.6" cy="19.2" r="0.9" fill="#EBE2CF" />
    <g style={{ transformOrigin: '22px 22px', animation: 'cdx-flutter 0.45s ease-in-out infinite' }}>
      <path d="M22 22c4-4 9-5 13-3-3 1-5 3-6 6-3 0-6-1-7-3z" fill="#E0892E" opacity="0.92" />
    </g>
    <path d="M30 31l8 3-8 0z" fill="#8E2C42" />
  </svg>
);

export const AuthShell = ({ kicker, title, subtitle, asideTitle, asideText, wide, children }) => (
  <div style={shell.page} className="cdx-grain">
    <div style={{ ...shell.frame, maxWidth: wide ? 1000 : 880 }} className="cdx-auth-frame">
      {/* Brand aside */}
      <aside style={shell.aside} className="cdx-grain cdx-auth-aside">
        <div className="cdx-greca--gold cdx-greca" style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
        <div style={shell.asideInner}>
          <AuthColibri size={72} />
          <h2 className="cdx-display" style={shell.asideTitle}>{asideTitle}</h2>
          <p style={shell.asideText}>{asideText}</p>
          <div style={shell.asideTags}>
            {['Plantas UNAM', 'Cocina inteligente', 'Huitzi IA'].map((t) => (
              <span key={t} style={shell.asideTag}>{t}</span>
            ))}
          </div>
        </div>
      </aside>

      {/* Form card */}
      <main style={shell.card} className="cdx-grain cdx-scroll">
        <div className="cdx-greca" style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
        <div style={shell.cardInner}>
          <span className="cdx-kicker">{kicker}</span>
          <h1 className="cdx-display" style={shell.title}>{title}</h1>
          {subtitle && <p style={shell.subtitle}>{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  </div>
);

/* ---- Form field primitives ---- */
export const Field = ({ label, name, type = 'text', value, onChange, error, disabled, autoComplete, autoFocus, icon, hint, required }) => (
  <label style={f.group}>
    <span style={f.label}>{label}{required && <span style={{ color: 'var(--cdx-copal)' }}> *</span>}</span>
    <span style={{ ...f.inputWrap, ...(error ? f.inputWrapErr : {}) }}>
      {icon && <span style={f.icon}>{icon}</span>}
      <input
        type={type} name={name} value={value} onChange={onChange}
        disabled={disabled} autoComplete={autoComplete} autoFocus={autoFocus}
        style={{ ...f.input, paddingLeft: icon ? 40 : 14 }}
      />
    </span>
    {error ? <span style={f.err}>{error}</span> : hint ? <span style={f.hint}>{hint}</span> : null}
  </label>
);

export const PasswordField = ({ label, name, value, onChange, error, disabled, autoComplete, hint, endSlot }) => {
  const [show, setShow] = useState(false);
  return (
    <label style={f.group}>
      <span style={f.label}>{label} <span style={{ color: 'var(--cdx-copal)' }}>*</span></span>
      <span style={{ ...f.inputWrap, ...(error ? f.inputWrapErr : {}) }}>
        <span style={f.icon}><LockGlyph /></span>
        <input
          type={show ? 'text' : 'password'} name={name} value={value} onChange={onChange}
          disabled={disabled} autoComplete={autoComplete}
          style={{ ...f.input, paddingLeft: 40, paddingRight: 76 }}
        />
        {endSlot && <span style={f.endSlot}>{endSlot}</span>}
        <button type="button" onClick={() => setShow((v) => !v)} style={f.toggle} aria-label={show ? 'Ocultar' : 'Mostrar'} tabIndex={-1}>
          {show ? <EyeOff /> : <Eye />}
        </button>
      </span>
      {error ? <span style={f.err}>{error}</span> : hint ? <span style={f.hint}>{hint}</span> : null}
    </label>
  );
};

const LockGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);
const Eye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
);
const EyeOff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A9.5 9.5 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3.2 3.8M6.1 6.1A17 17 0 0 0 2 12s4 7 10 7a9.5 9.5 0 0 0 2.1-.2" strokeLinecap="round" /></svg>
);

const shell = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '32px 16px', fontFamily: 'var(--cdx-font-body)',
    background: 'radial-gradient(120% 90% at 85% 10%, #ECDDC4 0%, #F4EBD9 55%, #F4EBD9 100%)',
  },
  frame: {
    width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1fr)',
    borderRadius: 'var(--cdx-r-xl)', overflow: 'hidden', boxShadow: 'var(--cdx-shadow-lg)',
    border: '1px solid var(--cdx-paper-edge)', background: 'var(--cdx-paper)',
  },
  aside: {
    position: 'relative', overflow: 'hidden',
    background: 'radial-gradient(120% 80% at 20% 10%, #1E2C24 0%, #143C28 60%, #0F1A13 100%)',
    display: 'flex', alignItems: 'center',
  },
  asideInner: { position: 'relative', zIndex: 1, padding: '40px 32px' },
  asideTitle: { fontSize: 26, color: 'var(--cdx-nighttext)', margin: '20px 0 12px', lineHeight: 1.15 },
  asideText: { fontSize: 14.5, color: 'var(--cdx-night-faint)', lineHeight: 1.6, marginBottom: 22 },
  asideTags: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  asideTag: {
    fontFamily: 'var(--cdx-font-mono)', fontSize: 11, color: 'var(--cdx-gold)',
    border: '1px solid color-mix(in srgb, #C9A227 40%, transparent)', borderRadius: 999, padding: '4px 11px',
  },
  card: { position: 'relative', overflow: 'auto', maxHeight: '92vh', background: 'var(--cdx-paper)' },
  cardInner: { position: 'relative', zIndex: 1, padding: '38px 36px' },
  title: { fontSize: 30, color: 'var(--cdx-ink)', margin: '8px 0 8px', lineHeight: 1.1 },
  subtitle: { fontSize: 14.5, color: 'var(--cdx-ink-soft)', lineHeight: 1.55, marginBottom: 22 },
};

const f = {
  group: { display: 'block', marginBottom: 14 },
  label: {
    display: 'block', marginBottom: 6, fontFamily: 'var(--cdx-font-mono)', fontSize: 10.5,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cdx-copal)', fontWeight: 500,
  },
  inputWrap: {
    display: 'flex', alignItems: 'center', position: 'relative',
    background: 'var(--cdx-paper-2)', border: '1px solid var(--cdx-paper-edge)',
    borderRadius: 'var(--cdx-r-md)',
  },
  inputWrapErr: { borderColor: 'var(--cdx-jamaica-soft)', background: 'var(--cdx-jamaica-wash)' },
  icon: { position: 'absolute', left: 13, color: 'var(--cdx-agave)', display: 'flex', pointerEvents: 'none' },
  input: {
    flex: 1, width: '100%', padding: '12px 14px', border: 'none', background: 'transparent',
    // 16px minimum: anything smaller makes iOS Safari auto-zoom on focus and
    // the app stays zoomed-in after login (user must pinch out).
    fontSize: 16, color: 'var(--cdx-ink)', fontFamily: 'var(--cdx-font-body)', outline: 'none',
    borderRadius: 'var(--cdx-r-md)',
  },
  endSlot: { position: 'absolute', right: 44, display: 'flex', alignItems: 'center' },
  toggle: {
    position: 'absolute', right: 8, background: 'transparent', border: 'none',
    color: 'var(--cdx-ink-faint)', cursor: 'pointer', padding: 6, display: 'flex',
  },
  err: { display: 'block', marginTop: 5, fontSize: 12, color: 'var(--cdx-jamaica)' },
  hint: { display: 'block', marginTop: 5, fontSize: 11.5, color: 'var(--cdx-ink-faint)' },
};

/* Responsive: stack on small screens via a media query injected once. */
if (typeof document !== 'undefined' && !document.getElementById('auth-shell-style')) {
  const el = document.createElement('style');
  el.id = 'auth-shell-style';
  el.textContent = `
    @media (max-width: 760px) {
      .cdx-auth-frame { grid-template-columns: 1fr !important; }
      .cdx-auth-aside { display: none !important; }
    }
  `;
  document.head.appendChild(el);
}
